// type: Webcomposant
// description: Segmented Item component for segmented control

import { EMPTY_STRING } from '@rotomeca/utils';
import { HTMLBnumIcon } from '../../../atoms/icon/bnum-icon';
import BnumElementInternal from '../../../bnum-element-states';
import { Nullable } from '@rotomeca/utils';
import {
	Attr,
	Data,
	Define,
	Fire,
	SetAttr,
	SetAttrs,
} from '../../../../core/decorators';
import { UI } from '../../../../core/decorators/ui';
import { Result, Risky } from '@rotomeca/rop';
import Log from '../../../../core/utils/Log';
import { Listen } from '../../../../core/decorators/listen';
import { Listener } from '../../../../core/decorators/listener';
import JsEvent from '@rotomeca/event';
import {
	BnumSegmentedItemUI,
	CallSelectArg,
	EventSelectArgs,
	OnSelectCallback,
	OnSelectListenerInitializer,
	SegmentedItemCreateOptions,
} from './bnum-segmented-item.internal';
import { States } from './bnum-segmented-item.internal/States';
import style from './bnum-segmented-item.less';
import { TAG_SEGMENTED_ITEM } from '../../../../core/utils/constants/tags';
import { UpdateAll } from 'core/decorators/updateAll';

//#region Global constants
// --- FLAT CONSTANTS ---

// Ids
const ID_ICON = 'icon';
const ID_CONTAINER = 'container';
const ID_LABEL = 'label';

// Selectors
const SEL_ICON = `#${ID_ICON}` as const;
const SEL_CONTAINER = `#${ID_CONTAINER}` as const;
const SEL_LABEL = `#${ID_LABEL}` as const;

// Events
const EVT_SELECTED = 'bnum-segmented-item:selected';
const EVT_ERROR = 'bnum-segmented-item:error';

// Attributes
const ATTR_SELECTED = 'selected';
const ATTR_DISABLED = 'disabled';

// States
const STATE_SELECTED = States.SELECTED as 'selected';
const STATE_DISABLED = States.DISABLED as 'disabled';
const STATE_ICON = States.ICON as 'icon';

// --- PUBLIC OBJECTS ---

/**
 * Evènements non-conventionnels
 */
const EVENTS = {
	SELECTED: EVT_SELECTED,
	ERROR: EVT_ERROR,
} as const;
//#endregion Global constants

//#region Template

const TEMPLATE = (
	<div class="segmented-item__container" id={ID_CONTAINER}>
		<HTMLBnumIcon class="segmented-item__container__icon" id={ID_ICON} />
		<span class="segmented-item__container__label" id={ID_LABEL}>
			<slot></slot>
		</span>
	</div>
);
//#endregion Template

/**
 * 
 * Composant représentant un item individuel au sein d'un contrôle segmenté.
 * 
 * @category SegmentedControl
 * 
 * @structure Defaut
 * <bnum-segmented-item value="item1" data-icon="home">Item 1</bnum-segmented-item>
 *
 * @structure Selected
 * <bnum-segmented-item value="item1" selected data-icon="home">Item 1</bnum-segmented-item>
 *
 * @structure Disabled
 * <bnum-segmented-item value="item1" disabled data-icon="home">Item 1</bnum-segmented-item>
 * 
 * @slot default - Le contenu textuel (label) de l'élément.
 * 
 * @state selected - quand l'élément est sélectionné.
 * @state disabled - quand l'élément est désactivé.
 * @state icon - quand une icône est définie via data-icon.
 * 
 * @attr {boolean} (optional) (default: false) disabled - Indique si l'item est désactivé. 
 * @attr {boolean} (optional) (default: false) selected - Indique si l'item est sélectionné.

 */
@Define({
	tag: TAG_SEGMENTED_ITEM,
	styles: style,
	template: TEMPLATE,
})
@UpdateAll()
export class HTMLBnumSegmentedItem extends BnumElementInternal {
	//#region Private Fields
	/** Références aux éléments du Shadow DOM. */
	@UI({
		icon: SEL_ICON,
		label: SEL_LABEL,
		container: SEL_CONTAINER,
	})
	accessor #_ui!: BnumSegmentedItemUI;
	//#endregion Private Fields

	//#region Getters/Setters
	/** @attr {boolean} (optional) (default: false) selected - État de sélection. */
	@Attr() accessor selected: boolean = false;

	/** @attr {boolean} (optional) (default: false) disabled - État désactivé. */
	@Attr() accessor disabled: boolean = false;

	/** @attr {string} (optional) (default: '') data-icon - Nom de l'icône à afficher. */
	@Data({ setter: false }) accessor #_icon: string = EMPTY_STRING;

	/** Instance JsEvent pour la souscription aux changements de sélection. */
	@Listener(OnSelectListenerInitializer)
	accessor onSelected!: JsEvent<OnSelectCallback>;

	/** @attr {string} value - Valeur technique de l'item. */
	get value(): string {
		return this.getAttribute('value') || this.innerText || EMPTY_STRING;
	}
	set value(value: string) {
		this.setAttribute('value', value);
	}

	/** Récupère le nom de l'icône actuelle. */
	get icon(): string {
		return this.#_icon;
	}

	/** Définit une nouvelle icône et met à jour le DOM. */
	set icon(value: string) {
		this.#_icon = value;
		this.#_updateIcon(value);
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
	}

	/** Initialise le DOM et les écouteurs. */
	@SetAttr('role', 'radio')
	protected _p_buildDOM(): void {
		this.#_updateDom().match({
			Ok: () => this.#_setListeners(),
			Err: error => this.#_dispatchError(error),
		});
	}

	/** Gère la mise à jour des attributs. */
	protected _p_update(
		_: string,
		__: string | null,
		___: string | null,
	): void | Nullable<'break'> {
		this.#_updateDom().tapError(error => this.#_dispatchError(error));
	}

	//#endregion Lifecycle

	//#region Public Methods
	/**
	 * Met à jour le texte du label via textContent.
	 * @param text Nouveau texte.
	 */
	@Risky()
	updateLabel(text: string): Result<void> {
		return this.#_verifyUi().tap(() => {
			this.textContent = text;
		});
	}

	/**
	 * Vérifie si l'élément possède un état spécifique.
	 * @param state État à vérifier.
	 */
	@Risky()
	is(state: States): Result<boolean> {
		let result;

		switch (state) {
			case States.SELECTED:
				result = this.hasAttribute(ATTR_SELECTED);
				break;

			case States.DISABLED:
				result = this.hasAttribute(ATTR_DISABLED);
				break;

			case States.ICON:
				result = this.#_icon !== EMPTY_STRING;
				break;

			default:
				throw new Error(`State "${state}" is not recognized.`);
		}

		return Result.Ok(result);
	}

	/**
	 * Désactive l'élément.
	 * @returns Cette instance pour le chaînage.
	 */
	disable(): this {
		this.disabled = true;
		return this;
	}

	/**
	 * Active l'élément.
	 * @returns Cette instance pour le chaînage.
	 */
	enable(): this {
		this.disabled = false;
		return this;
	}

	/**
	 * Sélectionne l'élément.
	 * @returns Cette instance pour le chaînage.
	 */
	select(): this {
		this.selected = true;
		return this;
	}

	/**
	 * Désélectionne l'élément.
	 * @returns Cette instance pour le chaînage.
	 */
	unselect(): this {
		this.selected = false;
		return this;
	}

	/**
	 * Met à jour la valeur technique de l'élément.
	 * @param value Nouvelle valeur.
	 * @returns Cette instance pour le chaînage.
	 */
	updateValue(value: string): this {
		this.value = value;
		return this;
	}

	/**
	 * Déclenche manuellement la logique de sélection.
	 *
	 * /!\ Ne modifie pas l'état sélectionné de l'élément.
	 * @param options Options incluant l'événement parent.
	 */
	callSelect({ parentEvent = null }: CallSelectArg = {}): void {
		const args: EventSelectArgs = {
			parentEvent,
			caller: this,
			value: this.value,
		};
		this.onSelected.call(args);
	}
	//#endregion Public Methods

	//#region Private Methods
	/** Synchronise l'ensemble du DOM avec les états internes. */
	@Risky()
	#_updateDom(): Result<void> {
		return this.#_verifyUi()
			.tap(() => this.#_clearStates())
			.tap(() => this.#_updateSelected())
			.tap(() => this.#_updateDisabled())
			.andThen(() => this.#_setIcon());
	}

	/** Vérifie la disponibilité des éléments UI du Shadow DOM. */
	@Risky()
	#_verifyUi(): Result<void> {
		if (!this.#_ui.icon || !this.#_ui.label || !this.#_ui.container) {
			Log.error('HTMLBnumSegmentedItem', 'UI not correctly initialized');
			throw new Error('UI not correctly initialized');
		}
		return Result.Ok();
	}

	/** Configure l'icône et active l'état associé. */
	@Risky()
	#_setIcon() {
		return this.#_updateIcon(this.#_icon).tap(() => {
			this._p_addState(STATE_ICON);
		});
	}

	/** Met à jour la propriété icon du webcomponent bnum-icon interne. */
	@Risky()
	#_updateIcon(icon: string): Result<void> {
		return this.#_verifyUi().tap(() => {
			this.#_ui.icon!.icon = icon;
		});
	}

	/** Réinitialise tous les états CSS custom. */
	#_clearStates() {
		this._p_clearStates();
		return this;
	}

	/** Met à jour l'UI en fonction de l'attribut selected. */
	#_updateSelected() {
		if (this.hasAttribute(ATTR_SELECTED)) this.#_select();
		else this.#_unselect();

		return this;
	}

	/** Met à jour l'UI en fonction de l'attribut disabled. */
	#_updateDisabled() {
		if (this.hasAttribute(ATTR_DISABLED)) this.#_disable();
		else this.#_enable();

		return this;
	}

	/** Active l'état désactivé. */
	@SetAttrs({ 'aria-disabled': 'true', tabindex: '-1' })
	#_disable() {
		this._p_addState(STATE_DISABLED);
	}

	/** Désactive l'état désactivé. */
	@SetAttr('aria-disabled', 'false')
	#_enable() {
		if (!this.hasAttribute(ATTR_SELECTED)) this.#_unselect();
	}

	/** Applique les attributs et états de sélection. */
	@SetAttrs({
		'aria-checked': 'true',
		tabindex: '0',
	})
	#_select() {
		this._p_addState(STATE_SELECTED);
	}

	/** Retire les attributs et états de sélection. */
	@SetAttrs({
		'aria-checked': 'false',
		tabindex: '-1',
	})
	#_unselect() {}

	/** Filtre le clic si l'item est désactivé ou déjà sélectionné. */
	#_onClickDisabled(e: Event) {
		const canBeClicked = !this.disabled && !this.selected;
		if (canBeClicked) return this.callSelect({ parentEvent: e });
	}

	/** Émet l'événement d'erreur. */
	@Fire(EVT_ERROR)
	#_dispatchError(error: Error) {
		return { error, caller: this };
	}

	/** Émet l'événement de sélection. */
	@Fire(EVT_SELECTED)
	#_onclick(e: Event) {
		return { event: e, caller: this, value: this.value };
	}

	/** Gère l'accessibilité clavier (Entrée/Espace). */
	#_onKeyDown(e: KeyboardEvent) {
		const canBeClicked = !this.disabled && !this.selected;
		const isEnterOrSpace = e.key === 'Enter' || e.key === ' ';
		if (canBeClicked && isEnterOrSpace) {
			e.preventDefault();
			return this.click();
		}
	}

	/** Initialise les écouteurs via décorateur @Listen. */
	#_setListeners() {
		this.#_listenClick();
		this.#_listenKeyDown();
	}

	/**
	 * Ecoute les clics sur l'élément.
	 * @returns Action à faire lors d'un clic.
	 */
	@Listen('click')
	#_listenClick() {
		return this.#_onClickDisabled;
	}

	/**
	 * Ecoute les événements clavier sur l'élément.
	 * @returns Action à faire lors d'un keydown.
	 */
	@Listen('keydown')
	#_listenKeyDown() {
		return this.#_onKeyDown;
	}

	//#endregion Private Methods

	//#region Protected Methods
	/**
	 * Déclencheur interne pour le pont avec l'initialiseur de Listener.
	 * @internal
	 */
	protected _p_onSelectedTrigger(e: Event): void {
		this.#_onclick(e);
	}
	//#endregion Protected Methods

	//#region Static Methods
	protected static _p_observedAttributes(): string[] {
		return [...super._p_observedAttributes(), ATTR_SELECTED, ATTR_DISABLED];
	}

	/**
	 * Crée un élément segmented-item.
	 * @param value Valeur technique de l'item.
	 * @param options Options de création.
	 * @returns L'élément créé.
	 */
	static Create(
		value: string,
		options?: SegmentedItemCreateOptions,
	): HTMLBnumSegmentedItem {
		const node = document.createElement(this.TAG) as HTMLBnumSegmentedItem;

		if (options?.onSelect)
			node.addEventListener(EVT_SELECTED, (e: any) => options.onSelect!(e));

		if (options?.OnError)
			node.addEventListener(EVT_ERROR, (e: any) =>
				options.OnError!(e.error, e.caller),
			);

		if (options?.label) node.textContent = options.label;

		return node
			.attr('value', value)
			.condAttr(
				options?.iconName as unknown as boolean,
				'data-icon',
				options!.iconName!,
			)
			.condAttr(options?.selected as boolean, ATTR_SELECTED, true)
			.condAttr(options?.disabled as boolean, ATTR_DISABLED, true);
	}

	/**
	 * Accès aux états disponibles pour cet élément.
	 */
	static get States() {
		return States;
	}

	/**
	 * Accès aux évènements non-conventionnels de cet élément.
	 */
	static get Events() {
		return EVENTS;
	}
	//#endregion Static Methods
}
